import { ActionStatus } from 'thunkless';
import produce from 'immer';

import { TemplatesTypes, whatsappFormKey } from '../actions/templates';

export interface MacroRecord {
  id: string;
  key: string | null;
  template_key: string | null;
  raw_text: string | null;
  hotkey: string | null;
  whatsapp_approved: boolean | null;
  whatsapp_template_linked?: boolean;
  media_url: string | null;
  media_mime: string | null;
  // Twilio Content API mirror of the template, present once it has been submitted to WhatsApp
  content_sid?: string | null;
  content_status?: 'pending' | 'received' | 'approved' | 'rejected' | 'failed' | string | null;
  content_category?: 'MARKETING' | 'UTILITY' | string | null;
  content_language?: string | null;
  content_rejection_reason?: string | null;
  content_submitted_at?: string | null;
}

// Shape returned by the templates_image_post endpoint — fetched on demand by
// the attachment uploader, never persisted in this slice (see actions/templates).
export interface S3PresignedPost {
  url: string;
  fields: Record<string, string>;
}

export type CustomVar = [string, string]; // [display label, $VAR_NAME]

export interface TemplatesState {
  fetchStatus: ActionStatus | null;
  // True once the first fetch has resolved (success or failure). Lets the UI
  // keep the list mounted through subsequent page/search refetches instead of
  // flashing the full-page loader.
  initialized: boolean;
  // The current page of macros only — ordering, pagination, and search are all
  // server-side, so this is never the account's full set.
  macros: MacroRecord[];
  page: number;
  totalPages: number;
  totalCount: number;
  search: string;
  customVars: CustomVar[];
  statusTypes: string[];
  isAdmin: boolean;
  canManage: boolean;
  isTrial: boolean;
  whatsappConnected: boolean;
  templateHotkeysEnabled: boolean;

  // Per-form mutation status keyed by form id:
  //   'create' for the Create Template form
  //   '<macro_id>' for each Current Templates row edit form
  formStatus: Record<string, ActionStatus | undefined>;
  formError: Record<string, string | undefined>;
}

export const initialState: TemplatesState = {
  fetchStatus: null,
  initialized: false,
  macros: [],
  page: 1,
  totalPages: 1,
  totalCount: 0,
  search: '',
  customVars: [],
  statusTypes: [],
  isAdmin: false,
  canManage: false,
  isTrial: false,
  whatsappConnected: false,
  templateHotkeysEnabled: false,
  formStatus: {},
  formError: {},
};

const errorMessageFromAction = (action: any, fallback: string): string => {
  const errors = action.payload?.error?.responseJSON?.errors
    ?? action.payload?.responseJSON?.errors;
  if (Array.isArray(errors) && errors.length) return errors.join(', ');
  return fallback;
};

export const reducer = produce((state: TemplatesState, action: any) => {
  switch (action.type) {
    case TemplatesTypes.FETCH_TEMPLATES_DATA_REQUEST: {
      state.fetchStatus = ActionStatus.BUSY;
      break;
    }
    case TemplatesTypes.FETCH_TEMPLATES_DATA_SUCCESS: {
      const { data } = action.payload;
      state.fetchStatus = ActionStatus.SUCCESS;
      state.initialized = true;
      state.macros = data.macros ?? [];
      state.page = data.page ?? 1;
      state.totalPages = Math.max(1, data.total_pages ?? 1);
      state.totalCount = data.total_count ?? 0;
      state.search = action.meta?.search ?? '';
      state.customVars = data.custom_vars ?? [];
      state.statusTypes = data.status_types ?? [];
      state.isAdmin = !!data.is_admin;
      state.canManage = !!data.can_manage;
      state.isTrial = !!data.is_trial;
      state.whatsappConnected = !!data.whatsapp_connected;
      state.templateHotkeysEnabled = !!data.template_hotkeys_enabled;
      break;
    }
    case TemplatesTypes.FETCH_TEMPLATES_DATA_FAILURE: {
      state.fetchStatus = ActionStatus.FAILURE;
      state.initialized = true;
      break;
    }

    case TemplatesTypes.CREATE_TEMPLATE_REQUEST: {
      state.formStatus.create = ActionStatus.BUSY;
      state.formError.create = undefined;
      break;
    }
    case TemplatesTypes.CREATE_TEMPLATE_SUCCESS: {
      // The list isn't patched locally — the new macro belongs wherever it falls
      // in the server's alphabetical order, so the view refetches the current
      // page instead (see TemplateActions#reload).
      state.formStatus.create = ActionStatus.SUCCESS;
      state.formError.create = undefined;
      break;
    }
    case TemplatesTypes.CREATE_TEMPLATE_FAILURE: {
      state.formStatus.create = ActionStatus.FAILURE;
      state.formError.create = errorMessageFromAction(action, 'Could not create the template.');
      break;
    }

    case TemplatesTypes.UPDATE_TEMPLATE_REQUEST: {
      const { id } = action.meta;
      state.formStatus[id] = ActionStatus.BUSY;
      state.formError[id] = undefined;
      break;
    }
    case TemplatesTypes.UPDATE_TEMPLATE_SUCCESS: {
      // No local patch — a renamed template can change its alphabetical slot, so
      // the view refetches the current page rather than reorder in place.
      const { id } = action.meta;
      state.formStatus[id] = ActionStatus.SUCCESS;
      state.formError[id] = undefined;
      break;
    }
    case TemplatesTypes.UPDATE_TEMPLATE_FAILURE: {
      const { id } = action.meta;
      state.formStatus[id] = ActionStatus.FAILURE;
      state.formError[id] = errorMessageFromAction(action, 'Could not save changes.');
      break;
    }

    case TemplatesTypes.SUBMIT_WHATSAPP_REQUEST:
    case TemplatesTypes.REFRESH_WHATSAPP_REQUEST: {
      const key = whatsappFormKey(action.meta.id);
      state.formStatus[key] = ActionStatus.BUSY;
      state.formError[key] = undefined;
      break;
    }
    case TemplatesTypes.SUBMIT_WHATSAPP_SUCCESS:
    case TemplatesTypes.REFRESH_WHATSAPP_SUCCESS: {
      const key = whatsappFormKey(action.meta.id);
      const updated: MacroRecord = action.payload.data;
      const index = state.macros.findIndex(({ id }) => id === updated.id);
      if (index !== -1) state.macros[index] = updated;
      state.formStatus[key] = ActionStatus.SUCCESS;
      state.formError[key] = undefined;
      break;
    }
    case TemplatesTypes.SUBMIT_WHATSAPP_FAILURE: {
      const key = whatsappFormKey(action.meta.id);
      state.formStatus[key] = ActionStatus.FAILURE;
      state.formError[key] = errorMessageFromAction(action, 'Could not submit this template to WhatsApp.');
      break;
    }
    case TemplatesTypes.REFRESH_WHATSAPP_FAILURE: {
      const key = whatsappFormKey(action.meta.id);
      state.formStatus[key] = ActionStatus.FAILURE;
      state.formError[key] = errorMessageFromAction(action, 'Could not check the WhatsApp status.');
      break;
    }

    case TemplatesTypes.DESTROY_TEMPLATE_REQUEST: {
      const { id } = action.meta;
      state.formStatus[id] = ActionStatus.BUSY;
      break;
    }
    case TemplatesTypes.DESTROY_TEMPLATE_SUCCESS: {
      // Drop the row immediately for a snappy delete; the view then refetches the
      // current page to backfill from the next page and correct the totals.
      const { id } = action.meta;
      if (state.macros.some((m) => m.id === id)) state.totalCount = Math.max(0, state.totalCount - 1);
      state.macros = state.macros.filter((m) => m.id !== id);
      delete state.formStatus[id];
      delete state.formError[id];
      break;
    }
    case TemplatesTypes.DESTROY_TEMPLATE_FAILURE: {
      const { id } = action.meta;
      // A client-error (4xx) on delete means the macro is already gone — the
      // backend's not-found guard (MacrosController#get_macro) returns 400, and
      // an actual delete is unblockable (Macro has no halting destroy callbacks),
      // so any 4xx here is "someone already deleted it." Reconcile locally so the
      // row doesn't linger. 5xx (transient server errors) fall through and keep
      // the row with an error so the user can retry.
      const httpStatus = action.payload?.error?.status ?? action.payload?.status;
      if (typeof httpStatus === 'number' && httpStatus >= 400 && httpStatus < 500) {
        if (state.macros.some((m) => m.id === id)) state.totalCount = Math.max(0, state.totalCount - 1);
        state.macros = state.macros.filter((m) => m.id !== id);
        delete state.formStatus[id];
        delete state.formError[id];
        break;
      }
      state.formStatus[id] = ActionStatus.FAILURE;
      state.formError[id] = errorMessageFromAction(action, 'Could not delete the template.');
      break;
    }

    case TemplatesTypes.CLEAR_TEMPLATE_FORM_ERROR: {
      const { formKey } = action.meta;
      state.formError[formKey] = undefined;
      if (state.formStatus[formKey] === ActionStatus.FAILURE) {
        state.formStatus[formKey] = undefined;
      }
      break;
    }
  }
}, initialState);
